/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { apiCall } from 'app/shared/lib/essette-api';
import { State } from 'app/shared/lib/state';
import { EventOptions, RuleActionOption, RuleConditionOption } from 'app/core/components/automations/automations-grid';
import { SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { I18N } from 'aurelia-i18n';
import { checkRuleActionOptionConfiguration, checkRuleConditionOptionConfiguration, RecordType } from 'app/core/components/automations/save-automation';

@autoinject()
export class ManageAutomationsPage {

    ////****************** Fields and Properties ************************************////
    protected moduleCode = 'UM';

    protected recordTypes: RecordType[] = [{ $display: 'Authorization', value: 'Auth' }];
    protected options: RuleConditionOption[] = [];
    protected actions: RuleActionOption[] = [];
    protected actionsLoaded: boolean = false;

    private sorting = new Sorting();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private state: State,
        private readonly logger: AppLogger,
        private service: AuthService,
        private i18n: I18N) {
        this.logger.source = this;
    }

    protected bind(): void {
        this.loadConditionOptions();
        this.loadActionOptions();
    }

    ////****************** Element References ***************************************////


    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadConditionOptions(): Promise<void> {
        try {
            this.state.beginLoad();

            this.sorting.entries = [
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];

            const options: Partial<IQueryLoadOptions> = {
                all: true,
                includeInactive: true,
                sorting: this.sorting
            }

            const [classesResponse, statusesResponse] = await Promise.all(
                [
                    this.service.loadAuthClasses(options, [], true),
                    this.service.loadAuthStatuses(options)
                ]
            );

            const classes = classesResponse.results;
            if(classes.length) {
                this.options.push({
                    $used: false,
                    expression: '',
                    parsedExpression: null,
                    translatedDisplay: this.i18n.tr('um:auth.class.andSubClass.$'),
                    options: classes.map(c => {
                        return { ...c, $selected: false, $expanded: false }
                    }),
                    displayKey: 'um:auth.class.andSubClass.$',
                    optionKey: 'um:auth.class.$',
                    optionLevel1: 'class',
                    optionLevel2: 'code',
                    subOptionKey: 'um:auth.class.subClass.$',
                    subOptionProperty: 'subClasses',
                    subOptionLevel1: 'class.subClass',
                    subOptionLevel2: 'code'
                });
            }

            const statuses = statusesResponse.results;
            if(statuses.length) {
                this.options.push({
                    $used: false,
                    expression: '',
                    parsedExpression: null,
                    translatedDisplay: this.i18n.tr('um:auth.status.$long'),
                    options: statuses.map(s => {
                        return { ...s, $selected: false, $expanded: false }
                    }),
                    displayKey: 'um:auth.status.$long',
                    optionKey: 'um:auth.status.$long',
                    optionLevel1: 'status',
                    optionLevel2: 'code',
                    excludeOnEvent: EventOptions.Insert
                });
            }

            this.options.forEach(option => checkRuleConditionOptionConfiguration(option));
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadActionOptions(): Promise<void> {
        try {
            this.state.beginLoad();

            this.sorting.entries = [
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];

            const options: Partial<IQueryLoadOptions> = {
                all: true,
                includeInactive: true,
                sorting: this.sorting
            };

            const queuesResponse = await this.service.loadQueues(options, [], false);

            const queues = queuesResponse.results;
            const payloadLevel2 = 'code';
            this.actions.push({
                translatedDisplay: this.i18n.tr('ae:local.routeTo', { what: this.i18n.tr('um:queue.$long') }),
                options: queues.map((o: any) => { return { ...o, payloadLevel2 } }),
                selectedOption: null,
                optionKey: 'um:queue.$long',
                payloadLevel1: 'queues',
                payloadLevel2,
                successActionType: 'UM.Auth.Route'
            });

            this.actions.forEach(action => checkRuleActionOptionConfiguration(action));

        } catch(error) {
            throw error;
        } finally {
            this.actionsLoaded = true;
            this.state.endLoad();
        }
    }

}
